import {
  ManagerCalculationRow,
  ManagerTaxCreditCalculation
} from '../models/manager-tax-credit-calculation.model';

// Shared rules for the Manager Tax Credit Calculation: building the applicant baseline and deciding
// what counts as a manager edit. Used by the manager card (highlighting, notes), the form actions
// (Approve gating) and the R&D fee formula (manager credit override), so all of them agree.

export const managerCalculationFields = [
  'prior0', 'prior1', 'prior2', 'average', 'base', 'current', 'increase', 'credit', 'laCredit',
  'prior0Fed', 'prior1Fed', 'prior2Fed', 'currentFed'
];

function round2(value: number): number {
  return value == null ? value : parseFloat(Number(value).toFixed(2));
}

// Builds the manager rows from the applicant's calculation year (also the baseline for edit detection).
export function buildManagerRowsFromApplicant(year: any, isSbir: boolean): ManagerCalculationRow[] {
  if (!year) {
    return [];
  }
  const row = new ManagerCalculationRow({
    yearLabel: year.yearLabel,
    taxYear: year.taxYear,
    prior0: year.threeYearsPriorExpenditure,
    prior1: year.twoYearsPriorExpenditure,
    prior2: year.oneYearPriorExpenditure,
    average: year.priorYearsAverage,
    base: year.baseCalculation,
    current: isSbir ? year.awardsReceived : year.currentYearExpenditure,
    increase: year.increaseInExpenditures,
    credit: year.creditPercentage,
    laCredit: year.louisianaResearchCredit,
    prior0Fed: year.threeYearsPriorExpenditureFed,
    prior1Fed: year.twoYearsPriorExpenditureFed,
    prior2Fed: year.oneYearPriorExpenditureFed,
    currentFed: year.currentYearExpenditureFed
  });
  row.prior0 = round2(row.prior0);
  row.prior1 = round2(row.prior1);
  row.prior2 = round2(row.prior2);
  row.average = round2(row.average);
  row.base = round2(row.base);
  row.current = round2(row.current);
  row.increase = round2(row.increase);
  row.laCredit = round2(row.laCredit);
  row.prior0Fed = round2(row.prior0Fed);
  row.prior1Fed = round2(row.prior1Fed);
  row.prior2Fed = round2(row.prior2Fed);
  row.currentFed = round2(row.currentFed);
  row.credit = row.credit == null ? row.credit : round2(row.credit * 100) / 100;
  return [row];
}

export function managerValuesDiffer(a: any, b: any): boolean {
  const an = a === null || a === undefined || a === '' ? null : Number(a);
  const bn = b === null || b === undefined || b === '' ? null : Number(b);
  if (an === null && bn === null) {
    return false;
  }
  if (an === null || bn === null) {
    return true;
  }
  return Math.abs(an - bn) > 1e-9;
}

export function managerRowsHaveEdits(
  rows: ManagerCalculationRow[],
  baselineRows: ManagerCalculationRow[]
): boolean {
  return (rows || []).some((row, i) => {
    const base = baselineRows[i];
    return !!base && managerCalculationFields.some(f => managerValuesDiffer(row[f], base[f]));
  });
}

export function isManagerCalculationNotesMissing(
  calc: ManagerTaxCreditCalculation,
  applicantYear: any,
  isSbir: boolean
): boolean {
  if (!calc || !applicantYear) {
    return false;
  }
  const edited = managerRowsHaveEdits(calc.rows, buildManagerRowsFromApplicant(applicantYear, isSbir));
  return edited && !(calc.notes && calc.notes.trim());
}
